import { SidebarModule } from '../contexts/ModuleContext';

/**
 * Sidebar sections — transporter-friendly work areas.
 * Order = visual order in the menu.
 */
export const SIDEBAR_GROUP_ORDER = [
  'Main',
  'People & Fleet',
  'Trips & Bilty',
  'Live Tracking',
  'Vehicle Care',
  'Accounts',
  'Reports',
  'Office',
  'Settings',
  'CRM',
] as const;

export type SidebarGroup = typeof SIDEBAR_GROUP_ORDER[number];

/** Section headers shown in the sidebar (empty = no header) */
export const SIDEBAR_GROUP_LABELS: Record<string, string> = {
  Main: '',
  'People & Fleet': 'People & Fleet',
  'Trips & Bilty': 'Trips & Bilty',
  'Live Tracking': 'Live Tracking',
  'Vehicle Care': 'Vehicle Care',
  Accounts: 'Accounts',
  Reports: 'Reports',
  Office: 'Office',
  Settings: 'Settings',
  CRM: 'CRM',
};

/**
 * Fixed TMS sidebar — clear groups, simple labels.
 */
export const FIXED_CORE_MODULES: Omit<SidebarModule, 'sortOrder'>[] = [
  { key: 'dashboard', label: 'Home', group: 'Main', icon: 'Dashboard', type: 'core', path: '/dashboard', isCore: true },

  { key: 'clients', label: 'Clients', group: 'People & Fleet', icon: 'Business', type: 'core', path: '/clients', isCore: true },
  { key: 'vehicles', label: 'Vehicles', group: 'People & Fleet', icon: 'DirectionsCar', type: 'core', path: '/vehicles', isCore: true },
  { key: 'vehicle_types', label: 'Vehicle Types', group: 'People & Fleet', icon: 'LocalShipping', type: 'core', path: '/vehicles/types', isCore: true, parentKey: 'vehicles' },
  { key: 'vehicle_makes', label: 'Brands', group: 'People & Fleet', icon: 'Build', type: 'core', path: '/vehicles/makes', isCore: true, parentKey: 'vehicles' },
  { key: 'vehicle_models', label: 'Models', group: 'People & Fleet', icon: 'DirectionsCarFilled', type: 'core', path: '/vehicles/models', isCore: true, parentKey: 'vehicles' },
  { key: 'vehicle_documents', label: 'Vehicle Documents', group: 'People & Fleet', icon: 'Folder', type: 'core', path: '/vehicles/documents', isCore: true, parentKey: 'vehicles' },
  { key: 'vehicle_vendors', label: 'Vehicle Vendors', group: 'People & Fleet', icon: 'Store', type: 'core', path: '/vehicle-vendors', isCore: true },
  { key: 'drivers', label: 'Drivers', group: 'People & Fleet', icon: 'Person', type: 'core', path: '/drivers', isCore: true },
  { key: 'helpers', label: 'Helpers', group: 'People & Fleet', icon: 'Groups', type: 'core', path: '/helpers', isCore: true },

  { key: 'trips', label: 'Trips', group: 'Trips & Bilty', icon: 'Route', type: 'core', path: '/trips', isCore: true },
  { key: 'bilty', label: 'Bilty (LR)', group: 'Trips & Bilty', icon: 'Description', type: 'core', path: '/bilty', isCore: true },
  { key: 'bilty_items', label: 'Goods Names', group: 'Trips & Bilty', icon: 'Inventory2', type: 'core', path: '/bilty/items', isCore: true, parentKey: 'bilty' },
  { key: 'consignors', label: 'Senders', group: 'Trips & Bilty', icon: 'LocalShipping', type: 'core', path: '/consignors', isCore: true, parentKey: 'bilty' },
  { key: 'consignees', label: 'Receivers', group: 'Trips & Bilty', icon: 'Inventory', type: 'core', path: '/consignees', isCore: true, parentKey: 'bilty' },
  { key: 'invoices', label: 'Bills', group: 'Trips & Bilty', icon: 'Receipt', type: 'core', path: '/invoices', isCore: true, parentKey: 'bilty' },

  { key: 'gps_tracking', label: 'Live Map (GPS)', group: 'Live Tracking', icon: 'GpsFixed', type: 'core', path: '/gps/live', isCore: true },
  { key: 'gps_devices', label: 'GPS Boxes', group: 'Live Tracking', icon: 'Router', type: 'core', path: '/gps/devices', isCore: true, parentKey: 'gps_tracking' },
  { key: 'gps_alerts', label: 'GPS Alerts', group: 'Live Tracking', icon: 'NotificationsActive', type: 'core', path: '/gps/alerts', isCore: true, parentKey: 'gps_tracking' },
  { key: 'gps_reports', label: 'GPS Reports', group: 'Live Tracking', icon: 'Assessment', type: 'core', path: '/gps/reports', isCore: true, parentKey: 'gps_tracking' },

  { key: 'fuel', label: 'Fuel', group: 'Vehicle Care', icon: 'LocalGasStation', type: 'core', path: '/fuel', isCore: true },
  { key: 'maintenance', label: 'Service / Repair', group: 'Vehicle Care', icon: 'Build', type: 'core', path: '/maintenance', isCore: true },
  { key: 'puncture', label: 'Puncture', group: 'Vehicle Care', icon: 'TireRepair', type: 'core', path: '/puncture', isCore: true },
  { key: 'battery_maintenance', label: 'Battery', group: 'Vehicle Care', icon: 'BatteryChargingFull', type: 'core', path: '/battery-maintenance', isCore: true },

  { key: 'cash_memo', label: 'Cash Book', group: 'Accounts', icon: 'ReceiptLong', type: 'core', path: '/cash-memo', isCore: true },
  { key: 'fastag', label: 'FASTag', group: 'Accounts', icon: 'Toll', type: 'core', path: '/fastag', isCore: true },
  { key: 'fleet_reports', label: 'Monthly Summary', group: 'Accounts', icon: 'Summarize', type: 'core', path: '/fleet-reports', isCore: true },

  { key: 'reports', label: 'Reports', group: 'Reports', icon: 'Assessment', type: 'core', path: '/reports', isCore: true },

  { key: 'branches', label: 'Branches', group: 'Office', icon: 'AccountTree', type: 'core', path: '/branches', isCore: true },
  { key: 'roles', label: 'Staff & Login', group: 'Office', icon: 'Group', type: 'core', path: '/roles', isCore: true },
  { key: 'activity', label: 'Change History', group: 'Office', icon: 'History', type: 'core', path: '/activity-log', isCore: true },

  { key: 'settings', label: 'Settings', group: 'Settings', icon: 'Settings', type: 'core', path: '/settings', isCore: true },
];

export const CORE_MODULE_KEYS = FIXED_CORE_MODULES.filter((m) => m.type === 'core').map((m) => m.key);

const CORE_ORDER = new Map(FIXED_CORE_MODULES.map((m, i) => [m.key, i]));

const byCoreThenLabel = (a: { key?: string; label: string }, b: { key?: string; label: string }) => {
  const ai = a.key != null && CORE_ORDER.has(a.key) ? CORE_ORDER.get(a.key)! : 10_000;
  const bi = b.key != null && CORE_ORDER.has(b.key) ? CORE_ORDER.get(b.key)! : 10_000;
  if (ai !== bi) return ai - bi;
  return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
};

/** @deprecated kept for imports — order is now fixed per group */
export const MENU_PRIORITY_KEYS = [
  'dashboard',
  'clients',
  'vehicles',
  'drivers',
  'helpers',
  'trips',
] as const;

/** Parents in fixed menu order; children under parent in fixed order */
export const sortSidebarParents = <T extends { key?: string; label: string; parentKey?: string; path?: string }>(
  parents: T[],
): T[] => [...parents].sort(byCoreThenLabel);

const sortModules = (items: SidebarModule[]) => {
  const parents = items.filter((m) => !m.parentKey);
  const children = items.filter((m) => m.parentKey);
  const orderedParents = sortSidebarParents(parents);

  const result: SidebarModule[] = [];
  orderedParents.forEach((parent) => {
    result.push({ ...parent, sortOrder: result.length });
    children
      .filter((c) => c.parentKey === parent.key)
      .sort(byCoreThenLabel)
      .forEach((child) => {
        result.push({ ...child, sortOrder: result.length });
      });
  });
  children
    .filter((c) => !orderedParents.some((p) => p.key === c.parentKey))
    .sort(byCoreThenLabel)
    .forEach((c) => result.push({ ...c, sortOrder: result.length }));
  return result.map((m, i) => ({ ...m, sortOrder: i }));
};

/** Map legacy / API group names into current sections */
const normalizeGroup = (group?: string): string => {
  if (!group) return 'CRM';
  const map: Record<string, string> = {
    Main: 'Main',
    Core: 'People & Fleet',
    Operations: 'Trips & Bilty',
    Fleet: 'Live Tracking',
    Maintenance: 'Vehicle Care',
    Accounts: 'Accounts',
    Documents: 'Reports',
    Organization: 'Office',
    Settings: 'Settings',
    CRM: 'CRM',
    'People & Fleet': 'People & Fleet',
    'Trips & Bilty': 'Trips & Bilty',
    'Live Tracking': 'Live Tracking',
    'Vehicle Care': 'Vehicle Care',
    Reports: 'Reports',
    Office: 'Office',
  };
  return map[group] || group;
};

export const buildGroupedSidebar = (modules: SidebarModule[]) => {
  const bucket = modules.reduce<Record<string, SidebarModule[]>>((acc, mod, index) => {
    const group = normalizeGroup(mod.group);
    if (!acc[group]) acc[group] = [];
    acc[group].push({ ...mod, group, sortOrder: mod.sortOrder ?? index });
    return acc;
  }, {});

  const ordered: Record<string, SidebarModule[]> = {};
  SIDEBAR_GROUP_ORDER.forEach((group) => {
    if (bucket[group]?.length) ordered[group] = sortModules(bucket[group]);
  });
  Object.keys(bucket).forEach((group) => {
    if (!ordered[group]) ordered[group] = sortModules(bucket[group]);
  });
  return ordered;
};

export const getDefaultSidebar = (): { modules: SidebarModule[]; grouped: Record<string, SidebarModule[]> } => {
  const modules: SidebarModule[] = FIXED_CORE_MODULES.map((m, i) => ({ ...m, sortOrder: i }));
  const flatSorted = sortModules(modules);
  return { modules: flatSorted, grouped: buildGroupedSidebar(flatSorted) };
};
